// Content data for each Veteran Services sub-page
// Keeping copy centralized so it can move into a CMS later without touching layout

export const VETERAN_SUB_SERVICES = {
  "evidence-development": {
    slug: "evidence-development",
    parentSlug: "/veteran-services",
    parentLabel: "Veteran Services",
    eyebrow: "Veteran Disability Consulting",
    title: "Evidence Development",
    tagline:
      "Building the record that tells your story clearly, accurately, and in the VA's language.",
    intro:
      "Most claims are won or lost on the strength of the evidence. We help you assemble, organize, and present your service records and medical documentation so the story of your service and its impact on your health is easy to follow.",
    services: [
      {
        title: "Record Reviews",
        desc: "We review your service treatment records, personnel records, and medical files to identify strong claim paths and gaps that need to be closed.",
      },
      {
        title: "Medical Evidence Strategy",
        desc: "Together we map which conditions to prioritize, what evidence is already there, and what specific documentation would meaningfully strengthen your file.",
      },
      {
        title: "Personal Statements",
        desc: "Guidance on writing personal statements that clearly describe onset, symptoms, and functional impact — the details that matter to raters.",
      },
      {
        title: "Buddy Statements",
        desc: "We coach you on how to request and structure buddy statements from fellow service members, family, or coworkers.",
      },
      {
        title: "Medical Documentation Guidance",
        desc: "We help you understand which records exist, how to request more from the VA or private providers, and how to interpret what's already in your file.",
      },
      {
        title: "Nexus Letter Coordination",
        desc: "Where medically appropriate, we help coordinate independent medical opinions that establish the medical link between service and current diagnosis.",
      },
    ],
    faqs: [
      {
        q: "Do I need a nexus letter for every claim?",
        a: "No. Many claims are decided on service records and treatment history alone. We only coordinate a nexus letter when it's medically appropriate and adds real value to your file.",
      },
      {
        q: "Can you write my personal statement for me?",
        a: "No — the statement must be yours. What we do is coach you on structure, key details to include, and clarity, so your voice comes through in a way the VA can act on.",
      },
      {
        q: "What if I don't have my service records?",
        a: "We can walk you through the process of requesting them and identify which agencies to contact.",
      },
    ],
  },

  "appointment-preparation": {
    slug: "appointment-preparation",
    parentSlug: "/veteran-services",
    parentLabel: "Veteran Services",
    eyebrow: "Veteran Disability Consulting",
    title: "Appointment Preparation",
    tagline:
      "Walk into every appointment — VA or private — knowing exactly what to say and why it matters.",
    intro:
      "C&P exams and doctor visits are pivotal moments. We prepare you to communicate your symptoms, functional loss, and history clearly so the picture in your medical record matches your reality.",
    services: [
      {
        title: "Primary Care Provider Preparation",
        desc: "We help you frame conversations with your PCP so ongoing treatment notes capture the depth and frequency of your symptoms.",
      },
      {
        title: "C&P Examination Preparation",
        desc: "Focused preparation for your Compensation & Pension exam — what the examiner is evaluating and how to describe your symptoms honestly and completely.",
      },
      {
        title: "Noble Strategies Personalized C&P Guide Method",
        desc: "Our proprietary preparation framework, tailored to your specific claim, conditions, and history.",
      },
      {
        title: "Functional Loss Education",
        desc: "Learn how to describe how your conditions actually affect daily activities — the details raters use to decide severity.",
      },
      {
        title: "Symptom Timeline Preparation",
        desc: "We help you construct a clear timeline of onset, flare-ups, and worsening — a document you can bring to any appointment.",
      },
      {
        title: "Medical Appointment Coaching",
        desc: "Short coaching calls before appointments so you go in confident, prepared, and clear.",
      },
    ],
    faqs: [
      {
        q: "Do you attend C&P exams with me?",
        a: "No. We are not attorneys or accredited representatives. We prepare you fully before the exam so you can advocate for yourself effectively.",
      },
      {
        q: "How long before a C&P should we prepare?",
        a: "Ideally 1–2 weeks. Enough time to review your file, refresh your memory on symptoms and timelines, and rehearse your talking points.",
      },
    ],
  },

  "decision-review": {
    slug: "decision-review",
    parentSlug: "/veteran-services",
    parentLabel: "Veteran Services",
    eyebrow: "Veteran Disability Consulting",
    title: "Decision Review",
    tagline:
      "Understand exactly why the VA decided what it did — and what to do next.",
    intro:
      "VA decision letters can be dense, confusing, and incomplete. We translate them into plain language and help you decide the strongest next step, whether that's a Supplemental Claim, a Higher-Level Review, or something else.",
    services: [
      {
        title: "Decision Letter Reviews",
        desc: "We break down every finding, favorable or unfavorable, and explain why the VA made each decision.",
      },
      {
        title: "Supplemental Claim Strategy",
        desc: "If new and relevant evidence exists, we help you build a Supplemental Claim that puts that evidence in front of a rater.",
      },
      {
        title: "Higher-Level Review Preparation",
        desc: "For claims that were decided incorrectly on the record, we help you prepare a focused Higher-Level Review request.",
      },
      {
        title: "Denial Analysis",
        desc: "A structured look at why a claim was denied and what — if anything — can change the outcome.",
      },
      {
        title: "Recommended Next Steps",
        desc: "A clear, prioritized action plan so you know what to do first, second, and third.",
      },
    ],
    faqs: [
      {
        q: "Can you appeal my decision for me?",
        a: "No. We are not attorneys or accredited VA representatives. We can educate you on your options and help you prepare — the submission is yours.",
      },
      {
        q: "Which review path is best for me?",
        a: "That depends on why the claim was denied and what new evidence, if any, exists. That's exactly what a Decision Review consultation is designed to answer.",
      },
    ],
  },

  "specialized-guidance": {
    slug: "specialized-guidance",
    parentSlug: "/veteran-services",
    parentLabel: "Veteran Services",
    eyebrow: "Veteran Disability Consulting",
    title: "Specialized Guidance",
    tagline:
      "Programs beyond the standard claim — helping you tap benefits you may not know about.",
    intro:
      "Combat-Related Special Compensation, Vocational Rehabilitation & Employment, education benefits, and transition assistance are powerful programs that go under-used because they're not well understood. We help you navigate them.",
    services: [
      {
        title: "CRSC (Combat-Related Special Compensation)",
        desc: "Guidance on eligibility, documentation, and the application process for CRSC.",
      },
      {
        title: "VR&E (Voc Rehab)",
        desc: "Overview of the VR&E process, entitlement determination, and how to prepare for your first meeting.",
      },
      {
        title: "Education Benefits",
        desc: "Understanding your GI Bill entitlement and how it interacts with other benefits and rating decisions.",
      },
      {
        title: "Transition Assistance",
        desc: "Practical guidance for veterans separating or recently separated — priorities, timelines, and common pitfalls.",
      },
    ],
    faqs: [
      {
        q: "Do you handle CRSC applications end-to-end?",
        a: "No, we provide education and documentation support. The application itself is submitted by you or your accredited representative.",
      },
      {
        q: "Is VR&E right for me?",
        a: "It depends on your rating, employment goals, and eligibility. A consultation can help you decide before you invest time in the application.",
      },
    ],
  },
};

export const VETERAN_SUB_SERVICES_LIST = Object.values(VETERAN_SUB_SERVICES);
